/**
 * Recursively repairs invalid table blocks to be compatible with BlockNote 0.13+
 * @param {Array} blocks - Array of BlockNote blocks
 * @returns {Array} - Array of blocks with repaired tables
 */
export const repairInvalidTables = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) return [];

  return blocks.map((block) => {
    // Process table blocks
    if (block.type === "table") {
      // More robust validation of table content
      const hasValidContent =
        block.content &&
        typeof block.content === 'object' &&
        Array.isArray(block.content.rows) &&
        block.content.rows.length > 0 &&
        block.content.rows.every(row =>
          row &&
          typeof row === 'object' &&
          Array.isArray(row.cells) &&
          row.cells.length > 0
        );

      if (hasValidContent) {
        // Table is already valid, but still process children
        if (block.children && Array.isArray(block.children) && block.children.length > 0) {
          return {
            ...block,
            children: repairInvalidTables(block.children)
          };
        }
        return block;
      }

      // Table is invalid - repair it

      // Create a default 2x2 table structure if content is missing or invalid
      // Based on BlockNote's tableContent format
      const defaultContent = {
        type: "tableContent",
        rows: [
          {
            cells: ["", ""]  // Empty cells as strings
          },
          {
            cells: ["", ""]
          }
        ]
      };

      // Try to preserve existing structure if possible
      let repairedContent = { ...defaultContent };

      // Only try to preserve if content exists and has rows array
      if (block.content &&
          typeof block.content === 'object' &&
          Array.isArray(block.content.rows) &&
          block.content.rows.length > 0) {
        // Preserve rows but ensure each has cells
        repairedContent.rows = block.content.rows.map(row => {
          if (!row || typeof row !== 'object' || !Array.isArray(row.cells) || row.cells.length === 0) {
            // Add default cells to invalid/empty row
            return {
              cells: ["", ""]  // Two empty cells
            };
          }
          return row;
        });
      }

      return {
        ...block,
        content: repairedContent,
        // Also recursively process children
        children: block.children && Array.isArray(block.children) && block.children.length > 0
          ? repairInvalidTables(block.children)
          : block.children
      };
    }

    // For non-table blocks, recursively process children if they exist
    if (block.children && Array.isArray(block.children) && block.children.length > 0) {
      return {
        ...block,
        children: repairInvalidTables(block.children)
      };
    }

    return block;
  });
};

/**
 * Recursively filters out invalid tables from blocks and their children
 * @param {Array} blocks - Array of BlockNote blocks
 * @returns {Array} - Array of blocks with invalid tables removed
 */
export const filterInvalidTables = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) return [];

  return blocks
    .filter((block) => {
      // Check if this is a table block
      if (block.type === "table") {
        // More defensive validation of table structure
        const hasValidContent =
          block.content &&
          typeof block.content === 'object' &&
          Array.isArray(block.content.rows) &&
          block.content.rows.length > 0 &&
          block.content.rows.every(row =>
            row &&
            typeof row === 'object' &&
            Array.isArray(row.cells) &&
            row.cells.length > 0
          );

        if (!hasValidContent) {
          console.log("Filtering out invalid table:", block.id, "Content:", block.content);
          return false;
        }
      }

      return true;
    })
    .map(block => {
      // Recursively filter children if they exist
      if (block.children && Array.isArray(block.children) && block.children.length > 0) {
        return {
          ...block,
          children: filterInvalidTables(block.children)
        };
      }
      return block;
    });
};

/**
 * Recursively removes empty heading blocks from the block tree.
 *
 * BlockNote renders a greyed "Heading" placeholder (via CSS ::before) for any
 * heading block with no text, even in read-only mode. When an author drops a
 * heading into a section and forgets to fill or remove it, that placeholder
 * leaks into the employee-facing view. Dropping the empty heading block before
 * render removes both the stray placeholder text and its orphan gap.
 *
 * Only headings are filtered. Empty paragraph blocks are intentionally used as
 * spacers (e.g. by the AI content generator), so they are left untouched.
 *
 * @param {Array} blocks - Array of BlockNote blocks
 * @returns {Array} - Array of blocks with empty headings removed
 */
export const filterEmptyHeadings = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) return [];

  return blocks
    .filter((block) => {
      if (block.type !== "heading") return true;

      const hasText =
        Array.isArray(block.content) &&
        block.content.some((inline) => (inline?.text || "").trim() !== "");
      const hasChildren =
        Array.isArray(block.children) && block.children.length > 0;

      return hasText || hasChildren;
    })
    .map((block) => {
      if (block.children && Array.isArray(block.children) && block.children.length > 0) {
        return {
          ...block,
          children: filterEmptyHeadings(block.children),
        };
      }
      return block;
    });
};

/**
 * Recursively removes ALL table blocks from the block tree (used as last-resort fallback)
 * @param {Array} blocks - Array of BlockNote blocks
 * @returns {Array} - Array of blocks with all tables removed
 */
export const removeAllTables = (blocks) => {
  if (!blocks || !Array.isArray(blocks)) return [];

  return blocks
    .filter(block => block.type !== "table")
    .map(block => {
      // Recursively remove tables from children if they exist
      if (block.children && Array.isArray(block.children) && block.children.length > 0) {
        return {
          ...block,
          children: removeAllTables(block.children)
        };
      }
      return block;
    });
};
